import { useState, useEffect } from "react";
import { TierBadge } from "../components";
import { useApp, TOPICS, api, timeAgo } from "../context";
import { Shell, Btn, EmptyState } from "../components";
import PostCard from "../PostCard";

const mapCommentRecursive = (list) => (list || []).map(r => ({
  id: r.id,
  authorId: r.user?.id,
  authorName: r.user?.name || "Unknown",
  authorEmoji: r.user?.avatar_emoji || "👤",
  authorPic: r.user?.avatar_url || null,
  authorVerified: r.user?.is_verified || false,
  authorPlan: r.user?.plan || "free",
  content: r.content,
  time: timeAgo(r.created_at),
  createdAt: r.created_at,
  likes: r.like_count || 0,
  liked: r.is_liked || false,
  sticker: r.sticker ? { id: r.sticker.id, url: r.sticker.url, name: r.sticker.name } : null,
  replies: mapCommentRecursive(r.allReplies || r.all_replies || r.replies || []),
}));

export const SearchPage = ({ searchQuery, doSearch, searchUsers, searchResults, setTopic }) => {
  const { c, go, setViewingUser } = useApp();
  return (
    <Shell title="Search">
      <div style={{ padding: "14px 0" }}>
        <div style={{ background: c.card, borderRadius: 13, border: `1px solid ${c.border}`, padding: "4px 4px 4px 14px", display: "flex", alignItems: "center", marginBottom: 18 }}>
          <span style={{ fontSize: 17, color: c.faint, marginRight: 8 }}>🔍</span>
          <input style={{ flex: 1, padding: "11px 0", border: "none", background: "transparent", color: c.text, fontSize: 14, outline: "none", fontFamily: "inherit" }} placeholder="Search posts, people, topics..." value={searchQuery} onChange={e => doSearch(e.target.value)} />
          {searchQuery && <button onClick={() => doSearch("")} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: c.input, color: c.sub, fontSize: 12, fontWeight: 600, cursor: "pointer", marginRight: 4 }}>✕</button>}
        </div>
        {!searchQuery ? (
          <>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 10 }}>Browse Topics</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9, marginBottom: 22 }}>
              {TOPICS.map(tp => (<button key={tp.id} onClick={() => { setTopic(tp.id); go("feed"); }} style={{ background: c.card, borderRadius: 13, border: `1px solid ${c.border}`, padding: 14, cursor: "pointer", textAlign: "left" }}><div style={{ fontSize: 26, marginBottom: 6 }}>{tp.icon}</div><div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{tp.name}</div></button>))}
            </div>
          </>
        ) : (
          <>
            {searchUsers.length > 0 && (<div style={{ marginBottom: 16 }}><h4 style={{ fontSize: 13, fontWeight: 700, color: c.sub, marginBottom: 8 }}>People</h4>{searchUsers.map(u => (<div key={u.id} onClick={() => { setViewingUser(u.id); go("user_profile"); }} style={{ display: "flex", alignItems: "center", gap: 11, padding: "10px 12px", borderRadius: 12, cursor: "pointer", background: c.card, border: `1px solid ${c.border}`, marginBottom: 6 }}><div style={{ width: 38, height: 38, borderRadius: 19, background: c.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>{u.emoji || "👤"}</div><div style={{ flex: 1 }}><span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{u.name}</span><TierBadge plan={u.plan} verified={u.verified} size={13} /><div style={{ fontSize: 11, color: c.sub }}>{u.bio}</div></div></div>))}</div>)}
            <h4 style={{ fontSize: 13, fontWeight: 700, color: c.sub, marginBottom: 8 }}>Posts ({searchResults.length})</h4>
            {searchResults.length === 0 ? <EmptyState icon="🔍" title="No posts found" /> : searchResults.map(p => <PostCard key={p.id} post={p} />)}
          </>
        )}
      </div>
    </Shell>
  );
};

export const PostDetailPage = ({ selectedPost, selectPost }) => {
  const { c, go, setExpandedComments, loadPostDetail } = useApp();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedPost?.id) { go("feed"); return; }
    setLoading(true);
    // Load directly from API — don't depend on posts array
    api(`/posts/${selectedPost.id}`).then(res => {
      if (res.ok && res.data?.post) {
        const p = res.data.post;
        const mapped = {
          id: p.id,
          topic: p.topic?.slug || "general",
          authorId: p.user?.id,
          authorName: p.user?.name || "Unknown",
          authorEmoji: p.user?.avatar_emoji || "👤",
          authorPic: p.user?.avatar_url || null,
          authorVerified: p.user?.is_verified || false,
          authorTrust: p.user?.trust_score || 0,
          authorPlan: p.user?.plan || "free",
          authorGender: p.user?.gender ? (p.user.gender === "Male" ? "M" : p.user.gender === "Female" ? "F" : p.user.gender === "Non-binary" ? "NB" : p.user.gender.charAt(0)) : null,
          content: p.content,
          likes: p.like_count || 0,
          comments: p.comment_count || 0,
          reposts: p.repost_count || 0,
          views: p.view_count || 0,
          time: timeAgo(p.created_at),
          ts: new Date(p.created_at).getTime(),
          liked: p.is_liked || false,
          bookmarked: p.is_bookmarked || false,
          isFollowing: p.is_following || false,
          poll: p.poll_data ? {
            id: p.poll_data.id,
            question: p.poll_data.question,
            totalVotes: p.poll_data.total_votes || 0,
            voted: p.poll_data.voted,
            endsIn: p.poll_data.ends_at ? Math.max(0, Math.ceil((new Date(p.poll_data.ends_at) - new Date()) / 86400000)) + "d" : "7d",
            options: (p.poll_data.options || []).map(o => ({ id: o.id, text: o.text, votes: o.votes || 0 }))
          } : null,
          commentList: (p.comments || []).map(cm => ({
            id: cm.id,
            authorId: cm.user?.id,
            authorName: cm.user?.name || "Unknown",
            authorEmoji: cm.user?.avatar_emoji || "👤",
            authorPic: cm.user?.avatar_url || null,
            authorVerified: cm.user?.is_verified || false,
            authorPlan: cm.user?.plan || "free",
            content: cm.content,
            time: timeAgo(cm.created_at),
            createdAt: cm.created_at,
            likes: cm.like_count || 0,
            liked: cm.is_liked || false,
            sticker: cm.sticker ? { id: cm.sticker.id, url: cm.sticker.url, name: cm.sticker.name } : null,
            replies: mapCommentRecursive(cm.allReplies || cm.all_replies || cm.replies || [])
          })),
          sticker: p.sticker ? { id: p.sticker.id, url: p.sticker.url, name: p.sticker.name } : null,
          mediaUrl: p.media_url || null,
          mediaType: p.media_type || null,
          mediaUrls: Array.isArray(p.media_urls) ? p.media_urls : (p.media_urls ? (() => { try { return JSON.parse(p.media_urls); } catch { return null; } })() : null),
          quotedPost: p.quoted_post ? { id: p.quoted_post.id, content: p.quoted_post.content, authorName: p.quoted_post.author_name, time: p.quoted_post.time } : null,
        };
        selectPost(mapped);
        setExpandedComments(prev => ({ ...prev, [p.id]: true }));
        // Also update global posts/trendingPosts arrays so other views stay in sync
        loadPostDetail(p.id);
      }
      setLoading(false);
    });
  }, [selectedPost?.id]); // eslint-disable-line

  if (!selectedPost) { go("feed"); return null; }

  return (
    <Shell title="Post" showBack onBack={() => { selectPost(null); go("feed"); }}>
      <div style={{ padding: "14px 0" }}>
        {loading
          ? <div style={{ textAlign: "center", padding: "60px 0", color: c.sub, fontSize: 14 }}>⏳ Loading...</div>
          : selectedPost
            ? <PostCard post={selectedPost} full />
            : <div style={{ textAlign: "center", padding: "60px 0", color: c.sub }}>Post not found</div>
        }
      </div>
    </Shell>
  );
};

export const NotificationsPage = ({ notifs, markNotifRead, markAllNotifsRead }) => {
  const { go, setViewingUser, setSelectedPost, loadPostDetail, posts, flash, dark } = useApp();
  const { c } = useApp();
  const [followedBack, setFollowedBack] = useState(
    () => JSON.parse(localStorage.getItem("gc_followed_back") || "[]")
  );
  return (
    <Shell title="Notifications" right={notifs.length > 0 ? <button onClick={markAllNotifsRead} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.card, color: c.pri, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>Mark all read</button> : null}>
      <div style={{ padding: "10px 0" }}>
        {notifs.length === 0 ? <EmptyState icon="🔔" title="No notifications yet" desc="You'll see likes, comments, and follows here" /> :
          notifs.map(n => {
            const goNotif = () => {
              markNotifRead(n.id);
              if (n.type === "like" || n.type === "comment" || n.type === "reply") {
                if (n.postId) {
                  loadPostDetail(n.postId).then(() => {
                    const post = posts.find(p => p.id === n.postId);
                    if (post) { setSelectedPost(post); go("post_detail"); }
                    else { go("feed"); }
                  });
                }
              } else if (n.type === "follow") {
                if (n.userId) { setViewingUser(n.userId); go("user_profile"); }
              } else if (n.type === "message") {
                go("messages");
              } else if (n.type === "tokens_received") {
                go("wallet");
              }
            };
            const typeStyle = {
              like:            { bg: dark ? "#72243E" : "#FBEAF0", fg: dark ? "#F4C0D1" : "#993556", icon: "❤️" },
              comment:         { bg: dark ? "#0C447C" : "#E6F1FB", fg: dark ? "#B5D4F4" : "#185FA5", icon: "💬" },
              reply:           { bg: dark ? "#0C447C" : "#E6F1FB", fg: dark ? "#B5D4F4" : "#185FA5", icon: "💬" },
              follow:          { bg: dark ? "#3C3489" : "#EEEDFE", fg: dark ? "#CECBF6" : "#3C3489", icon: "👥" },
              tokens_received: { bg: dark ? "#633806" : "#FAEEDA", fg: dark ? "#FAC775" : "#854F0B", icon: "💰" },
              mention:         { bg: dark ? "#3C3489" : "#EEEDFE", fg: dark ? "#CECBF6" : "#3C3489", icon: "@" },
            }[n.type] || { bg: c.card, fg: c.faint, icon: "🔔" };
            const accentColor = { like: "#D4537E", comment: "#378ADD", reply: "#378ADD", follow: "#7F77DD", tokens_received: "#EF9F27" }[n.type] || c.pri;
            return (
              <div key={n.id} onClick={goNotif} style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px", cursor: "pointer", position: "relative" }}>
                {!n.read && <div style={{ position: "absolute", left: 0, top: 8, bottom: 8, width: 3, background: accentColor }} />}
                <div style={{ width: 40, height: 40, borderRadius: 20, background: typeStyle.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, marginLeft: 8 }}>
                  {typeStyle.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: n.read ? c.sub : c.text }}>{n.text}</div>
                  <div style={{ fontSize: 12, color: c.faint, marginTop: 4 }}>{n.time}</div>
                </div>
                {n.type === "follow" && n.userId ? (
                  <button onClick={async e => {
                    e.stopPropagation();
                    markNotifRead(n.id);
                    const res = await api(`/profile/${n.userId}/follow`, { method: "POST" });
                    if (res.ok) {
                      flash("Following back! 🎉");
                      const updated = [...followedBack, n.userId];
                      setFollowedBack(updated);
                      localStorage.setItem("gc_followed_back", JSON.stringify(updated));
                    }
                  }} style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: followedBack.includes(n.userId) ? c.grn : c.pri, fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                    {followedBack.includes(n.userId) ? "✓ Following" : "Follow Back"}
                  </button>
                ) : !n.read ? (
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: accentColor, flexShrink: 0, marginTop: 6 }} />
                ) : null}
              </div>
            );
          })
        }
      </div>
    </Shell>
  );
};